import _ from 'underscore';
import React, {
  createRef, useCallback, useContext, useEffect, useMemo, useState,
} from 'react';
import { mergeClasses } from '@fluentui/react-components';
import {
  processor, withSiteMap, navRefreshSiteMap,
} from '@bingads-webui-react/with-site-map';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { PagesSearchEngine } from '@bingads-webui-react/global-search';

import { ToolsDialogConfig, MarginStyles, SiteMapItem } from './types';
import { ToolsPanelContainer } from './tools-panel-container';
import { ToolsContent } from './tools-content';
import { Searchbox, SearchboxLiveRegion } from './searchbox';
import { ToolsContentNoResults } from './tools-content-no-results';
import { getFilteredConfig, getResultsCount, updateConfigWithSiteMap } from './utils';
import { useDialogStyles } from './styles';

const ToolsPanelInternal: React.FC<{
  config: ToolsDialogConfig;
  open: boolean;
  onClose: () => void;
  overlayMargins?: MarginStyles;
  siteMap: SiteMapItem[];
  scope: any;
  userContext: any;
  onClick?: (item) => void;
}> = ({
  config, open, onClose, overlayMargins, siteMap, scope, userContext, onClick = () => {},
}) => {
  const classes = useDialogStyles();

  const searchboxRef = createRef<HTMLInputElement>();
  const [searchValue, setSearchValue] = useState<string>('');
  const i18n = useContext(I18nContext);

  const newConfig = useMemo(() => (
    updateConfigWithSiteMap(config, siteMap, scope, userContext, onClick)
  ), [config, siteMap, scope, userContext]);

  const searchEngine = useMemo(() => new PagesSearchEngine({
    searchItemsTreeRoot: { children: siteMap },
    i18n,
  }), [siteMap]);

  useEffect(() => {
    if (open && searchboxRef.current) {
      searchboxRef.current.focus();
    }
  }, [open, searchboxRef]);

  const onSearchValueChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
  }, []);

  const filteredConfig = useMemo(() => getFilteredConfig(newConfig, searchEngine, searchValue), [newConfig, searchValue]);
  const totalCount = useMemo(() => getResultsCount(filteredConfig), [filteredConfig]);

  return (
    <ToolsPanelContainer
      open={open}
      onClose={onClose}
      overlayMargins={overlayMargins}
    >
      <div className={mergeClasses('tools-dialog-content', classes.root)}>
        <Searchbox
          ref={searchboxRef}
          value={searchValue}
          onChange={onSearchValueChange}
        />
        <SearchboxLiveRegion count={totalCount} />
        {!_.isEmpty(filteredConfig.groups)
          ? (<ToolsContent config={filteredConfig} />)
          : (<ToolsContentNoResults />)}
      </div>
    </ToolsPanelContainer>
  );
};

type ToolsPanelWrapperProps = {
  config: ToolsDialogConfig;
  open: boolean;
  onClose: () => void;
  overlayMargins?: MarginStyles;
  userContext: any;
  onClick?: (item) => void;
  processorSet?: typeof processor[];
};

export const ToolsPanel: React.FC<ToolsPanelWrapperProps> = ({ processorSet = [], ...rest }) => {
  const ToolsPanelWithSiteMap = useMemo(() => withSiteMap(ToolsPanelInternal, [...processorSet], navRefreshSiteMap), [processorSet]);
  return <ToolsPanelWithSiteMap {...rest} />;
};
